/**
 * The daily coverage report (docs/product/reports.md) as pure data: rows from
 * the `coverage` view in, report rows and totals out. The screen and both
 * exports read this one result, so an export cannot disagree with the view it
 * was taken from.
 */
export type CoverageViewRow = {
  date: string
  team_id: string
  shift_template_id: string | null
  assignment_id: string | null
  is_shift_total: boolean
  total_assigned: number
  total_available: number
  total_on_duty: number
  total_on_leave: number
  total_detailed_out: number
  // numeric in the database, so it may arrive as a string
  planned_overtime_hours: number | string
  required_staffing: number | null
  variance: number | null
  has_unfilled_assignment: boolean
  has_qualification_shortage: boolean
  coverage_state: 'understaffed' | 'at-risk' | 'adequately-staffed' | 'overstaffed' | null
}

export type CoverageLookups = {
  teams: Map<string, string>
  // Team id to the id of the account that supervises it.
  supervisors: Map<string, string>
  shifts: Map<string, string>
  assignments: Map<string, string>
}

export type CoverageReportStatus =
  | 'understaffed'
  | 'at-risk'
  | 'adequately-staffed'
  | 'overstaffed'
  | 'missing-qualification'
  | 'no-target'

export const COVERAGE_REPORT_STATUS_LABEL: Record<CoverageReportStatus, string> = {
  understaffed: 'Understaffed',
  'at-risk': 'At risk',
  'adequately-staffed': 'Adequately staffed',
  overstaffed: 'Overstaffed',
  'missing-qualification': 'Missing required qualification',
  'no-target': 'No target',
}

export const WHOLE_SHIFT_LABEL = 'Whole shift'
export const UNASSIGNED_LABEL = 'Unassigned'
// An officer whose record the caller can no longer read, such as one who has transferred to another supervisor's team.
export const UNKNOWN_OFFICER = 'Name unavailable'

export type CoverageReportRow = {
  key: string
  date: string
  team: string
  teamId: string
  supervisorId: string | null
  shift: string
  shiftId: string | null
  assignment: string
  assignmentId: string | null
  required: number | null
  scheduled: number
  onDuty: number
  available: number
  // Available headcount against required, capped at the requirement; null
  // where no target applies.
  fillPercent: number | null
  onLeave: number
  detailedOut: number
  plannedOvertime: number
  unfilledRole: boolean
  qualificationGap: boolean
  status: CoverageReportStatus
}

export type CoverageReportFilters = {
  from?: string
  to?: string
  teamId?: string
  supervisorId?: string
  shiftId?: string
  assignmentId?: string
  status?: CoverageReportStatus
}

export type CoverageReportColumn =
  | 'date'
  | 'team'
  | 'shift'
  | 'assignment'
  | 'required'
  | 'scheduled'
  | 'available'
  | 'fillPercent'
  | 'onLeave'
  | 'detailedOut'
  | 'unfilledRole'
  | 'qualificationGap'
  | 'status'

export type CoverageReportSort = { column: CoverageReportColumn; direction: 'asc' | 'desc' }

export type CoverageReportTotals = {
  required: number
  scheduled: number
  available: number
  onLeave: number
  detailedOut: number
  unfilledRoles: number
  qualificationGaps: number
}

// A headcount that meets its target but not a required qualification reads as
// its own state (coverage-and-insights.md); an understaffed row stays
// understaffed, since the headcount is the larger problem.
function statusFor(row: CoverageViewRow): CoverageReportStatus {
  if (row.coverage_state === null) return 'no-target'
  if (row.coverage_state !== 'understaffed' && row.has_qualification_shortage) return 'missing-qualification'
  return row.coverage_state
}

export function toReportRows(rows: CoverageViewRow[], lookups: CoverageLookups): CoverageReportRow[] {
  return rows.map((row) => ({
    key: [row.date, row.team_id, row.shift_template_id ?? '', row.is_shift_total ? 'shift' : (row.assignment_id ?? 'none')].join('|'),
    date: row.date,
    team: lookups.teams.get(row.team_id) ?? 'Unknown team',
    teamId: row.team_id,
    supervisorId: lookups.supervisors.get(row.team_id) ?? null,
    shift: row.shift_template_id ? (lookups.shifts.get(row.shift_template_id) ?? 'Unknown shift') : 'No shift',
    shiftId: row.shift_template_id,
    assignment: row.is_shift_total
      ? WHOLE_SHIFT_LABEL
      : row.assignment_id
        ? (lookups.assignments.get(row.assignment_id) ?? 'Unknown assignment')
        : UNASSIGNED_LABEL,
    assignmentId: row.assignment_id,
    required: row.required_staffing,
    scheduled: row.total_assigned,
    onDuty: row.total_on_duty,
    available: row.total_available,
    fillPercent:
      row.required_staffing === null || row.required_staffing === 0
        ? null
        : Math.round((Math.min(row.total_available, row.required_staffing) / row.required_staffing) * 100),
    onLeave: row.total_on_leave,
    detailedOut: row.total_detailed_out,
    plannedOvertime: Number(row.planned_overtime_hours),
    unfilledRole: row.has_unfilled_assignment,
    qualificationGap: row.has_qualification_shortage,
    status: statusFor(row),
  }))
}

export function filterReportRows(rows: CoverageReportRow[], filters: CoverageReportFilters): CoverageReportRow[] {
  return rows.filter(
    (row) =>
      (!filters.from || row.date >= filters.from) &&
      (!filters.to || row.date <= filters.to) &&
      (!filters.teamId || row.teamId === filters.teamId) &&
      (!filters.supervisorId || row.supervisorId === filters.supervisorId) &&
      (!filters.shiftId || row.shiftId === filters.shiftId) &&
      (!filters.assignmentId || row.assignmentId === filters.assignmentId) &&
      (!filters.status || row.status === filters.status),
  )
}

function compareNullable(a: number | string | boolean | null, b: number | string | boolean | null): number {
  // A missing value sorts after every present one in either direction.
  if (a === null && b === null) return 0
  if (a === null) return 1
  if (b === null) return -1
  if (typeof a === 'string' && typeof b === 'string') return a.localeCompare(b)
  return Number(a) - Number(b)
}

export function sortReportRows(rows: CoverageReportRow[], sort: CoverageReportSort): CoverageReportRow[] {
  const direction = sort.direction === 'asc' ? 1 : -1
  return [...rows].sort((a, b) => {
    const left = a[sort.column]
    const right = b[sort.column]
    if (left === null || right === null) return compareNullable(left, right)
    return compareNullable(left, right) * direction || a.key.localeCompare(b.key)
  })
}

// A shift-wide row restates the total of the assignments in its shift, so
// summing it with them would count the same people twice.
export function totalsFor(rows: CoverageReportRow[]): CoverageReportTotals {
  const totals: CoverageReportTotals = {
    required: 0,
    scheduled: 0,
    available: 0,
    onLeave: 0,
    detailedOut: 0,
    unfilledRoles: 0,
    qualificationGaps: 0,
  }
  for (const row of rows) {
    if (row.assignment === WHOLE_SHIFT_LABEL) continue
    totals.required += row.required ?? 0
    totals.scheduled += row.scheduled
    totals.available += row.available
    totals.onLeave += row.onLeave
    totals.detailedOut += row.detailedOut
    if (row.unfilledRole) totals.unfilledRoles += 1
    if (row.qualificationGap) totals.qualificationGaps += 1
  }
  return totals
}

export type FillRate = { filled: number; required: number; percent: number | null }

// Of the headcount the staffing targets require, how much was available. Each
// assignment contributes at most its own requirement, so a surplus in one
// does not cover a shortage in another. An assignment with no target is not
// counted, and a shift-wide row restates its assignments, so it is left out.
export function fillRateFor(rows: CoverageReportRow[]): FillRate {
  let filled = 0
  let required = 0
  for (const row of rows) {
    if (row.assignment === WHOLE_SHIFT_LABEL || row.required === null) continue
    required += row.required
    filled += Math.min(row.available, row.required)
  }
  return { filled, required, percent: required === 0 ? null : Math.round((filled / required) * 100) }
}

export function fillRateLabel(rate: FillRate): string {
  return rate.percent === null ? 'No target' : `${rate.percent}%`
}

export type CoverageSummaryRow = CoverageReportTotals & {
  date: string
  status: CoverageReportStatus
  fillRate: FillRate
}

// The status a day reads as when its rows disagree: the one a supervisor most
// needs to see wins.
const STATUS_SEVERITY: CoverageReportStatus[] = [
  'understaffed',
  'missing-qualification',
  'at-risk',
  'overstaffed',
  'adequately-staffed',
  'no-target',
]

// The status a group of rows reads as, by the same order.
export function worstStatus(statuses: CoverageReportStatus[]): CoverageReportStatus {
  return STATUS_SEVERITY.find((candidate) => statuses.includes(candidate)) ?? 'no-target'
}

// The summary view: one row per date, with the same totals rule as the
// detailed view.
export function summarizeByDate(rows: CoverageReportRow[]): CoverageSummaryRow[] {
  const dates = Array.from(new Set(rows.map((row) => row.date))).sort()
  return dates.map((date) => {
    const dayRows = rows.filter((row) => row.date === date)
    const status = worstStatus(dayRows.map((row) => row.status))
    return { date, status, fillRate: fillRateFor(dayRows), ...totalsFor(dayRows) }
  })
}
